import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Menu,
  Bell,
  Check,
  CheckCheck,
  Trash2,
  FileText,
  MessageSquare,
  Info,
  X,
} from "lucide-react";
import useAuthStore from "../../store/authStore";
import { GetAxios, PatchAxios, DeleteAxios } from "../../helpers/axiosHelpers";

const NOTIFICATION_ICONS = {
  new_application: FileText,
  new_inquiry: MessageSquare,
  system: Info,
  info: Info,
};

const NOTIFICATION_COLORS = {
  new_application: "bg-blue-100 text-subidha-blue",
  new_inquiry: "bg-green-100 text-green-600",
  system: "bg-amber-100 text-amber-600",
  info: "bg-slate-100 text-slate-600",
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const AdminHeader = ({ title, subtitle, setIsOpen, onProfileClick }) => {
  const { user } = useAuthStore();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const getInitials = (name) => {
    if (!name) return "AD";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await GetAxios("/notifications/unread-count");
      if (res.success) setUnreadCount(res.data.count);
    } catch {
      // silently fail
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await GetAxios("/notifications");
      if (res.success) setNotifications(res.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll unread count every 30 seconds
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Load notifications when dropdown opens
  useEffect(() => {
    if (showNotifications) fetchNotifications();
  }, [showNotifications, fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    if (showNotifications) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showNotifications]);

  const markAsRead = async (id) => {
    try {
      await PatchAxios(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await PatchAxios("/notifications/mark-all-read");
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() })));
      setUnreadCount(0);
    } catch {}
  };

  const deleteNotification = async (id) => {
    try {
      await DeleteAxios(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setUnreadCount((c) => {
        const wasUnread = notifications.find((n) => n.id === id && !n.isRead);
        return wasUnread ? Math.max(0, c - 1) : c;
      });
    } catch {}
  };

  const clearAll = async () => {
    try {
      await DeleteAxios("/notifications/clear-all");
      setNotifications([]);
      setUnreadCount(0);
    } catch {}
  };

  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-100 px-6 py-4 flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => setIsOpen(true)}
          className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-xl transition-all"
        >
          <Menu size={24} />
        </button>

        <div>
          <h1 className="text-xl font-black text-slate-900 tracking-tight">
            {title}
          </h1>
          <p className="hidden md:block text-[10px] font-black text-slate-400 uppercase tracking-widest">
            {subtitle}
          </p>
        </div>
      </div>

      <div className="flex items-center space-x-3">
        {/* Notification Bell */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowNotifications((v) => !v)}
            className="p-2.5 text-slate-400 hover:text-subidha-blue hover:bg-blue-50 rounded-xl transition-all relative"
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center px-1 border-2 border-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-96 max-h-[480px] bg-white rounded-2xl shadow-2xl border border-slate-100 flex flex-col overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
              {/* Header */}
              <div className="p-4 border-b border-slate-100 flex items-center justify-between shrink-0">
                <div>
                  <h3 className="text-sm font-black text-slate-900">Notifications</h3>
                  {unreadCount > 0 && (
                    <p className="text-[10px] font-bold text-slate-400 mt-0.5">
                      {unreadCount} unread
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllRead}
                      className="p-1.5 text-slate-400 hover:text-subidha-blue hover:bg-blue-50 rounded-lg transition-all"
                      title="Mark all read"
                    >
                      <CheckCheck size={16} />
                    </button>
                  )}
                  {notifications.length > 0 && (
                    <button
                      onClick={clearAll}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      title="Clear all"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => setShowNotifications(false)}
                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* Notification List */}
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-6 h-6 border-2 border-subidha-blue border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <Bell size={32} className="mb-3 opacity-30" />
                    <p className="text-xs font-bold uppercase tracking-wider">
                      No notifications
                    </p>
                  </div>
                ) : (
                  notifications.map((notif) => {
                    const IconComp = NOTIFICATION_ICONS[notif.type] || Info;
                    const colorClass = NOTIFICATION_COLORS[notif.type] || "bg-slate-100 text-slate-600";

                    return (
                      <div
                        key={notif.id}
                        className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50/80 transition-colors group ${
                          !notif.isRead ? "bg-blue-50/40" : ""
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${colorClass}`}>
                          <IconComp size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs leading-snug ${!notif.isRead ? "font-black text-slate-900" : "font-bold text-slate-600"}`}>
                            {notif.title}
                          </p>
                          <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">
                            {notif.message}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-1 font-bold">
                            {timeAgo(notif.createdAt)}
                          </p>
                        </div>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          {!notif.isRead && (
                            <button
                              onClick={() => markAsRead(notif.id)}
                              className="p-1 text-slate-400 hover:text-subidha-blue hover:bg-blue-50 rounded transition-all"
                              title="Mark as read"
                            >
                              <Check size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => deleteNotification(notif.id)}
                            className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                            title="Delete"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <div className="h-8 w-px bg-slate-100 mx-2" />
        <button
          onClick={onProfileClick}
          className="flex items-center space-x-3 p-1 pr-4 hover:bg-slate-50 rounded-2xl transition-all group"
        >
          <div className="w-10 h-10 bg-gradient-to-br from-subidha-blue to-blue-600 rounded-xl flex items-center justify-center text-white font-black text-xs shadow-lg shadow-blue-500/10 group-hover:shadow-blue-500/30 transition-all">
            {getInitials(user?.name)}
          </div>
          <span className="hidden sm:block text-xs font-black text-slate-700 group-hover:text-subidha-blue transition-colors">
            {user?.name?.split(" ")[0] || "Admin"}
          </span>
        </button>
      </div>
    </header>
  );
};

export default AdminHeader;
